// ============================================================================
// SANDBOX APPROVAL QUEUE
// Admin component to review and approve/reject sandbox environment requests
// ============================================================================

import React, { useState } from 'react';
import {
  Cloud,
  CheckCircle,
  XCircle,
  Clock,
  User,
  DollarSign,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useSandboxQueue, useApproveSandbox, useRejectSandbox } from '../../hooks/useSandbox';

interface SandboxApprovalQueueProps {
  organizationId: string;
}

const SandboxApprovalQueue: React.FC<SandboxApprovalQueueProps> = ({ organizationId }) => {
  const { data: requests, isLoading, error } = useSandboxQueue();
  const approveMutation = useApproveSandbox();
  const rejectMutation = useRejectSandbox();

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [budgetOverrides, setBudgetOverrides] = useState<Record<string, number>>({});

  const handleApprove = async (projectId: string) => {
    try {
      await approveMutation.mutateAsync({
        projectId,
        budgetOverride: budgetOverrides[projectId],
      });
    } catch (err: any) {
      alert(`Failed to approve: ${err.message}`);
    }
  };

  const handleReject = async (projectId: string) => {
    try {
      await rejectMutation.mutateAsync({
        projectId,
        reason: rejectionReason || undefined,
      });
      setRejectingId(null);
      setRejectionReason('');
    } catch (err: any) {
      alert(`Failed to reject: ${err.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 size={20} className="animate-spin mr-2" />
        Loading pending requests...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
        <AlertTriangle size={16} />
        Failed to load approval queue: {error.message}
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="text-center py-16">
        <Cloud size={48} className="text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-1">No Pending Requests</h3>
        <p className="text-sm text-slate-500">
          Sandbox environment requests will appear here for approval.
        </p>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.sandboxEnvironment.status === 'requested').length;
  const failedCount = requests.filter(r => r.sandboxEnvironment.status === 'failed').length;
  const provisioningCount = requests.filter(r => r.sandboxEnvironment.status === 'provisioning').length;

  const summaryParts: string[] = [];
  if (pendingCount > 0) summaryParts.push(`${pendingCount} pending`);
  if (provisioningCount > 0) summaryParts.push(`${provisioningCount} provisioning`);
  if (failedCount > 0) summaryParts.push(`${failedCount} failed`);

  return (
    <div>
      <p className="text-sm text-slate-500 mb-5">
        {summaryParts.join(', ')} &middot; {requests.length} total
      </p>

      <div className="flex flex-col gap-4">
        {requests.map(({ project, sandboxEnvironment: env }) => {
          const isRequested = env.status === 'requested';
          const isFailed = env.status === 'failed';
          const isProvisioning = env.status === 'provisioning';

          return (
            <div
              key={project.projectId}
              className={`bg-white rounded-xl border shadow-sm p-5 ${
                isFailed ? 'border-red-200' : 'border-slate-200'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{project.name}</h3>
                  {project.description && (
                    <p className="text-xs text-slate-500 mt-0.5">{project.description}</p>
                  )}
                </div>
                <StatusBadge status={env.status} />
              </div>

              {/* Error banner */}
              {isFailed && env.provisioningError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5 mb-4 text-xs text-red-800 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-500" />
                  <span>{env.provisioningError}</span>
                </div>
              )}

              {/* Details grid */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <DetailItem icon={Cloud} label="Provider" value={env.provider.toUpperCase()} />
                <DetailItem icon={DollarSign} label="Budget" value={env.budgetLimit ? `$${env.budgetLimit}/mo` : 'Not set'} />
                <DetailItem icon={User} label="Requested By" value={env.requestedBy || '—'} />
                <DetailItem icon={Clock} label="Requested" value={env.requestedAt ? new Date(env.requestedAt).toLocaleDateString() : '—'} />
              </div>

              {/* Budget override — only for pending */}
              {isRequested && (
                <div className="mb-4">
                  <label className="block text-xs text-slate-500 mb-1">Budget Override (optional)</label>
                  <input
                    type="number"
                    placeholder={env.budgetLimit?.toString() || '100'}
                    value={budgetOverrides[project.projectId] || ''}
                    onChange={(e) => setBudgetOverrides(prev => ({
                      ...prev,
                      [project.projectId]: Number(e.target.value)
                    }))}
                    className="w-40 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-colors"
                  />
                </div>
              )}

              {/* Rejection reason */}
              {isRequested && rejectingId === project.projectId && (
                <div className="mb-4">
                  <label className="block text-xs text-slate-500 mb-1">Rejection Reason</label>
                  <input
                    type="text"
                    placeholder="Optional reason for rejection"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-colors"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                {isRequested && (
                  <>
                    <button
                      onClick={() => handleApprove(project.projectId)}
                      disabled={approveMutation.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    >
                      {approveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                      Approve
                    </button>

                    {rejectingId === project.projectId ? (
                      <>
                        <button
                          onClick={() => handleReject(project.projectId)}
                          disabled={rejectMutation.isPending}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {rejectMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                          Confirm Reject
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                          className="px-4 py-2 text-xs font-medium rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setRejectingId(project.projectId)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      >
                        <XCircle size={14} /> Reject
                      </button>
                    )}
                  </>
                )}

                {(isFailed || isProvisioning) && (
                  <button
                    onClick={() => handleApprove(project.projectId)}
                    disabled={approveMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {approveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Retry Provisioning
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { classes: string; icon: React.ElementType; label: string; spin?: boolean }> = {
    requested: { classes: 'bg-amber-50 text-amber-600 border-amber-200', icon: Clock, label: 'Pending Approval' },
    provisioning: { classes: 'bg-blue-50 text-blue-600 border-blue-200', icon: Loader2, label: 'Provisioning', spin: true },
    failed: { classes: 'bg-red-50 text-red-600 border-red-200', icon: AlertTriangle, label: 'Failed' },
  };
  const c = config[status] || { classes: 'bg-slate-50 text-slate-500 border-slate-200', icon: Clock, label: status };
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${c.classes}`}>
      <Icon size={12} className={c.spin ? 'animate-spin' : ''} />
      {c.label}
    </span>
  );
};

const DetailItem: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} className="text-slate-400 shrink-0" />
    <div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-xs font-medium text-slate-700">{value}</div>
    </div>
  </div>
);

export default SandboxApprovalQueue;
